import { useEffect, useContext, useRef, useCallback } from "react";
import { apiGet } from "../../api/apiGet";
import { LoginContext } from "../../contexts/LoginContext";
import { getCookie } from "../../helpers/helperFunctions";
import { BASE_API_URL } from "../../helpers/const";

function Login(props) {
  const { isAuthenticated, setAuthenticated, setUsername, setGroups } =
    useContext(LoginContext);

  const allgroups = props.groups;

  // Activity tracking refs
  const lastActivityTimeRef = useRef(Date.now());
  const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds

  // Function to recheck authentication
  const recheckAuthentication = useCallback(async () => {
    if (!isAuthenticated) return;

    console.log(
      "Rechecking authentication due to activity after timeout period"
    );
    try {
      const login = await apiGet.checkLogin();
      if (login?.status !== 200) {
        console.log("Session expired, logging out user");
        setAuthenticated(false);
        setUsername("");
        setGroups([]);
        window.location.href = BASE_API_URL;
      } else {
        console.log("Authentication check passed - session is still valid");
      }
    } catch (error) {
      console.log("Authentication check failed, logging out user");
      setAuthenticated(false);
      setUsername("");
      setGroups([]);
      window.location.href = BASE_API_URL;
    }
  }, [isAuthenticated, setAuthenticated, setUsername, setGroups]);

  // Activity event handler - checks if timeout has passed and triggers recheck
  const handleUserActivity = useCallback(() => {
    if (!isAuthenticated) return;

    const currentTime = Date.now();
    const timeSinceLastActivity = currentTime - lastActivityTimeRef.current;

    // If more than INACTIVITY_TIMEOUT has passed since last activity, recheck auth
    if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
      recheckAuthentication();
    }

    // Update last activity time
    lastActivityTimeRef.current = currentTime;
  }, [isAuthenticated, recheckAuthentication, INACTIVITY_TIMEOUT]);

  // Set up activity listeners when user is authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Reset activity time when user becomes authenticated
    lastActivityTimeRef.current = Date.now();

    // Activity events to listen for
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
    ];

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [isAuthenticated, handleUserActivity]);

  useEffect(() => {
    async function checkLogin() {
      try {
        const login = await apiGet.checkLogin();
        if (login?.status === 200) {
          setAuthenticated(true);
          const { groups } = await apiGet.getGroups();
          let tempGroups = [];
          for (let group of groups) {
            if (allgroups.includes(group)) {
              tempGroups = [...tempGroups, group];
            }
          }
          setGroups(tempGroups);
          const u = getCookie("username");
          setUsername(u);
        }
      } catch (error) {
        window.location.href = BASE_API_URL;
        setAuthenticated(false);
      }
    }
    if (!isAuthenticated) {
      checkLogin();
    }
  }, [isAuthenticated, allgroups, setAuthenticated, setGroups, setUsername]);
}
export default Login;
